import { useMemo, useState } from "react";
import { CATEGORIES, ME_SECTIONS, ROUTES } from "@oshimap/config";
import type { MeSection } from "@oshimap/config";
import { normalizeCategoryCode, favoriteKey, activeRoles, type RoleCode , secondaryHandle } from "@oshimap/domain";
import { ep, session } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { useFavorites } from "../favorite/use-favorites";
import { navigate } from "../../app/router";
import { Button, CategoryChip, Chip, EmptyState, ErrorState, Skeleton, VerificationBadge, CategoryIcon } from "../../components/ui";
import { GuestState } from "../../components/GuestState";
import { play } from "../../lib/motion";
import { OshiPane } from "../oshi/OshiPane";
import { NotificationPane } from "../notification/NotificationPane";
import { ReportsPane } from "../report/ReportsPane";
import "./me.css";
import { useAuthPrompt } from "../auth/useAuthPrompt";
import { AuthSheet } from "../auth/AuthSheet";
import { queryKeys } from "@oshimap/query";

/** 내부 역할 코드를 사람 말로. 없는 코드는 코드 그대로 둔다 — 지어내지 않는다. */
const ROLE_LABEL: Readonly<Partial<Record<RoleCode, string>>> = {
  SUPER_ADMIN: "최고 관리자",
  ADMIN: "관리자",
  MODERATOR: "검수자",
  OFFICIAL_MANAGER: "공식 계정 관리자",
};

const TAB_LABEL: Record<MeSection, string> = { saved: "저장", oshi: "최애", reports: "제보", notifications: "알림", account: "계정" };

function toSection(v: string | undefined): MeSection {
  return ME_SECTIONS.includes(v as MeSection) ? (v as MeSection) : "saved";
}

export function MeScreen({ section, reportId }: { section?: string; reportId?: string }) {
  const auth = useAuthPrompt();
  /* 탭이 곧 주소다. 새로고침·공유·뒤로가기가 같은 화면으로 돌아온다 (IA-4). */
  const pane = toSection(section);
  const user = session.user;
  const staffRoles = user ? activeRoles(user).filter((r) => r !== "USER") : [];

  if (!user) {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">MY</h1>
        <GuestState signedOut={{ title: "로그인하면 내 것이 모입니다", body: "저장한 곳, 최애, 방문 기록, 제보 상태를 한곳에서 볼 수 있어요." }} auth={auth} />
        {auth.reason ? <AuthSheet reason={auth.reason} onClose={auth.close} /> : null}
      </div>
    );
  }

  return (
    <div className="om-screen om-me">
      <header className="om-me__head">
        <span className="om-me__avatar" aria-hidden="true">🐧</span>
        <div>
          <h1 className="om-me__name">
            {user.displayName}
            {/*
              * suffix 가 없는 계정은 handle 이 displayName 과 똑같다 (서버의
              * public_handle 이 그렇게 대체한다). 그대로 두면 같은 이름이 두 번
              * 찍혀 버그처럼 보인다. 다를 때만 덧붙인다.
              */}
            {secondaryHandle(user)
              ? <> <span className="num om-me__handle">{secondaryHandle(user)}</span></>
              : null}
          </h1>
          {/*
            * 일반 사용자에게 "USER" 라고 적어 봐야 아무 뜻이 없다 — 내부 코드일 뿐이다.
            * 권한이 따로 있는 사람에게만, 그 권한만 보여준다.
            */}
          {staffRoles.length > 0
            ? <p className="om-me__role">{staffRoles.map((r) => ROLE_LABEL[r] ?? r).join(" · ")}</p>
            : null}
        </div>
      </header>

      <div className="om-me__pad">
        <div className="om-segtabs" role="tablist" aria-label="MY 보기">
          {ME_SECTIONS.map((k) => (
            <button key={k} role="tab" aria-selected={pane === k}
              onClick={() => navigate(`${ROUTES.me}/${k}`)}>{TAB_LABEL[k]}</button>
          ))}
        </div>
      </div>

      {pane === "saved" ? <SavedList />
      : pane === "oshi" ? <OshiPane />
      : pane === "reports" ? <ReportsPane highlightId={reportId} />
      : pane === "notifications" ? <NotificationPane />
      : <AccountPane />}
    </div>
  );
}

/** FR-SAVE-01/08: 저장한 곳을 다시 찾을 수 있어야 저장이 의미를 갖는다. */
function SavedList() {
  const fav = useFavorites();
  const [onlyPlaces, setOnlyPlaces] = useState(true);

  const favs = useQuery(
    [...queryKeys.favorites(), session.state.status],
    (signal) => ep.listFavorites({ signal }),
  );
  const places = useQuery(
    queryKeys.places({ pageSize: 100 }),
    (signal) => ep.listPlaces({ pageSize: 100 }, { signal }),
  );

  const saved = useMemo(() => {
    if (favs.state.status !== "success" || places.state.status !== "success") return [];
    const keys = new Set(favs.state.data.map((f) => favoriteKey({ targetType: f.targetType as "PLACE", targetId: f.targetId })));
    return places.state.data.filter((p) => keys.has(`PLACE:${p.id}`));
  }, [favs.state, places.state]);

  const loading = favs.state.status === "loading" || places.state.status === "loading";
  const failed = favs.state.status === "error" || places.state.status === "error";

  return (
    <div className="om-me__pad">
      <div className="om-chiprow" style={{ marginBottom: "var(--s-4)" }}>
        <Chip pressed={onlyPlaces} onClick={(e) => { setOnlyPlaces(true); play(e.currentTarget, "mo-pop"); }}>장소</Chip>
        <Chip pressed={!onlyPlaces} onClick={(e) => { setOnlyPlaces(false); play(e.currentTarget, "mo-pop"); }}>이벤트</Chip>
      </div>

      {loading ? <Skeleton lines={3} />
      : failed ? <ErrorState title="저장 목록을 불러오지 못했어요" onRetry={() => { favs.reload(); places.reload(); }} />
      : !onlyPlaces ? (
        <EmptyState title="저장한 이벤트가 없어요" body="이벤트 상세에서 하트를 누르면 여기에 모입니다." />
      ) : saved.length === 0 ? (
        <EmptyState
          title="아직 저장한 곳이 없어요"
          body="지도나 상세 화면에서 하트를 누르면 여기에 모입니다."
          action={<Button variant="primary" size="sm" onClick={() => navigate(ROUTES.map)}>지도 보기</Button>}
        />
      ) : (
        <>
          <p className="om-count num">{saved.length}곳</p>
          <div className="om-cards" style={{ padding: 0 }}>
            {saved.map((p) => (
              <div key={p.id} className="om-card">
                <button className="om-card__thumb" aria-hidden="true" tabIndex={-1} onClick={() => navigate(ROUTES.place(p.id))}>
                  <CategoryIcon code={p.category.code} size={26} />
                </button>
                <button className="om-card__body" onClick={() => navigate(ROUTES.place(p.id))} style={{ textAlign: "left" }}>
                  <span className="om-card__name">{p.name}</span>
                  <span className="om-card__row"><CategoryChip code={p.category.code} /></span>
                  <span className="om-card__row">
                    <VerificationBadge status={p.verificationStatus} lastVerifiedAt={p.lastVerifiedAt} />
                  </span>
                </button>
                <button className="om-fav" aria-pressed="true" aria-label={`${p.name} 저장 해제`}
                  onClick={async (e) => { play(e.currentTarget, "mo-pop"); await fav.toggle("PLACE", p.id); favs.reload(); }}>
                  ♥
                </button>
              </div>
            ))}
          </div>
          {fav.error ? <p className="om-note om-note--err" role="status">{fav.error}</p> : null}
        </>
      )}
    </div>
  );
}

function AccountPane() {
  const user = session.user!;
  const staff = activeRoles(user).filter((r) => r !== "USER");
  return (
    <div className="om-me__pad">
      <ul className="om-rowlist">
        <li><span>이메일</span><b>{user.email}</b></li>
        <li><span>핸들</span><b className="num">{user.handle}</b></li>
        {/* 권한이 따로 없는 사람에게 "USER" 한 줄을 보여줄 이유가 없다. */}
        {staff.length > 0
          ? <li><span>권한</span><b>{staff.map((r) => ROLE_LABEL[r] ?? r).join(", ")}</b></li>
          : null}
      </ul>
      <Button variant="secondary" block style={{ marginTop: "var(--s-4)" }}
        onClick={() => navigate(ROUTES.meSettings)}>
        설정
      </Button>
      <Button variant="ghost" block style={{ marginTop: "var(--s-2)" }}
        onClick={() => { void session.signOut(); }}>
        로그아웃
      </Button>
    </div>
  );
}
